/**
 * The app's way of saying something happened when the screen already moved on.
 *
 * Every write in Jarvis used to be modal in spirit: the control went dead, the
 * page waited for the network, and a failure landed as a line of small red text
 * at the bottom of whichever card you were not looking at. Once writes became
 * optimistic — the row disappears the moment you tick it — there is nothing
 * left to gate on and nowhere for a failure to appear. That is what this is
 * for: the screen reacts instantly, and the rare bad news comes here.
 *
 * Deliberately small. No queue priorities, no positions, no promise wrappers.
 * A message, a tone, and at most one thing to do about it.
 */

import { createContext, useContext } from "react";
import type { Tone } from "../components/StatusDot";

/** The subset of the app's tone vocabulary a toast can wear. */
export type ToastTone = Extract<Tone, "ok" | "danger" | "info">;

export interface ToastAction {
  label: string;
  /** Running it also dismisses the toast — an offer taken is an offer spent. */
  onSelect: () => void;
}

export interface ToastInput {
  /** What happened, in the user's terms. One short sentence. */
  message: string;
  /**
   * Why, in the machine's terms — the reason from `describeError`.
   *
   * Kept separate rather than concatenated onto `message`: "Не вдалося
   * зберегти «X»." followed by four lines about starting the backend reads as
   * one undifferentiated wall, and the half that says which row failed is the
   * half that has to be legible at a glance.
   */
  detail?: string;
  /** Defaults to `danger`: the only thing that *must* be announced is failure. */
  tone?: ToastTone;
  action?: ToastAction;
  /** Time on screen. Defaults by tone; an action always buys extra. */
  durationMs?: number;
  /**
   * Identity for repeats. A second toast with the same key replaces the first
   * instead of stacking — the backend being down fires once per write, and
   * four copies of "немає зв'язку" say nothing the first one didn't.
   */
  key?: string;
}

export type ShowToast = (toast: ToastInput) => void;

export const ToastContext = createContext<ShowToast | null>(null);

/**
 * Show a toast. Outside a `ToastHost` this is a no-op that complains in the
 * console: a component rendered alone in a test should not crash, but an
 * unmounted host in the real app would silently swallow every error report,
 * and silence is the failure mode this whole file exists to prevent.
 */
export function useToast(): ShowToast {
  const show = useContext(ToastContext);
  if (show) return show;
  return (toast: ToastInput) => {
    console.error("[toast] no ToastHost mounted, message dropped:", toast.message);
  };
}
